import { useEffect, useMemo, useRef, useState } from "react";
import { LumikaLogo } from "@lumika/ui";
import { EmailCodeForm } from "../components/EmailCodeForm";

type GoogleButtonStatus = "loading" | "ready" | "missing-client-id" | "unavailable";

type GoogleIdentity = {
  initialize: (config: {
    client_id: string;
    login_uri: string;
    ux_mode: "redirect";
    auto_select: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: "standard";
      theme: "filled_black";
      size: "large";
      shape: "rectangular";
      text: "signin_with";
      logo_alignment: "left";
      width: number;
    },
  ) => void;
};

declare global {
  interface Window {
    __lumikaGoogleIdentityConfigKey?: string;
    google?: {
      accounts?: {
        id?: GoogleIdentity;
      };
    };
  }
}

/**
 * Where the pre-login URL is stashed across the Google redirect flow.
 * Login ends at "/" (backend AUTH_SUCCESS_REDIRECT_PATH), so the index
 * route in App.tsx reads-and-clears this to land the user back on the
 * deep link they originally opened.
 */
export const POST_LOGIN_PATH_KEY = "lumika.postLoginPath";

export function SignInPage() {
  const error = new URLSearchParams(window.location.search).get("error");
  const loginUri = useMemo(() => `${window.location.origin}/google/auth/callback`, []);
  const clientId = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID ?? "";
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonStatus, setButtonStatus] = useState<GoogleButtonStatus>("loading");

  useEffect(() => {
    const { pathname, search } = window.location;
    // Auth-flow destinations are not deep links: "/" (including the
    // /?error=… bounce from a failed login) and the explicit sign-out
    // target "/sign-in" must CLEAR any stash from an earlier abandoned
    // login rather than resurrect or create one.
    const authPaths = ["/", "/sign-in", "/select-dealership"];
    if (!authPaths.includes(pathname)) {
      window.sessionStorage.setItem(POST_LOGIN_PATH_KEY, pathname + search);
    } else {
      window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    }
  }, []);

  useEffect(() => {
    if (!clientId) {
      setButtonStatus("missing-client-id");
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    const renderGoogleButton = () => {
      const parent = buttonRef.current;
      const googleIdentity = window.google?.accounts?.id;

      if (cancelled || !parent) return;

      if (!googleIdentity) {
        attempts += 1;
        if (attempts >= 80) {
          setButtonStatus("unavailable");
          return;
        }
        timer = window.setTimeout(renderGoogleButton, 100);
        return;
      }

      try {
        parent.innerHTML = "";
        const configKey = `${clientId}|${loginUri}|app`;
        if (window.__lumikaGoogleIdentityConfigKey !== configKey) {
          googleIdentity.initialize({
            client_id: clientId,
            login_uri: loginUri,
            ux_mode: "redirect",
            auto_select: false,
          });
          window.__lumikaGoogleIdentityConfigKey = configKey;
        }
        googleIdentity.renderButton(parent, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
          width: Math.min(parent.clientWidth || 360, 360),
        });
        setButtonStatus("ready");
      } catch {
        setButtonStatus("unavailable");
      }
    };

    renderGoogleButton();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (buttonRef.current) buttonRef.current.innerHTML = "";
    };
  }, [clientId, loginUri]);

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6 text-foreground">
      <section className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <LumikaLogo theme="auto" size={48} />
        <div className="grid gap-2">
          <h1 className="text-4xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm text-muted-foreground">
            Use the Google or email account your dealership administrator invited.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Access was not granted for this Google account.
          </div>
        )}

        <div
          ref={buttonRef}
          aria-live="polite"
          className="flex min-h-[44px] w-full items-center justify-center"
        />

        {buttonStatus === "loading" && (
          <p className="text-xs text-muted-foreground">Loading Google sign-in…</p>
        )}
        {buttonStatus === "missing-client-id" && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Google sign-in is not configured for this app.
          </div>
        )}
        {buttonStatus === "unavailable" && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Google sign-in could not load. Check your browser network settings and try again.
          </div>
        )}

        <div className="flex w-full items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or use email</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <EmailCodeForm
          onSuccess={(kind) => {
            if (kind === "session") {
              window.location.assign("/");
            } else {
              window.location.assign("/select-dealership");
            }
          }}
        />
      </section>
    </main>
  );
}
